import React, { useState } from 'react';

import { formatCurrency } from '../../../utils/Utils';
import MarketplaceSellersDialog from '../../Dashboard/MarketplaceSellersDialog';
import MarketplacePriceBand from './MarketplacePriceBand';
import { formatMarketplaceRank, ownAwareMarketplaceLabel, operatorTextOptions, priceBandMarkers } from './marketplaceProfile';

// Product detail hero of the marketplace operator profile: the operator's own
// price, the buybox and the best competitor marketplace, then a price band with
// every marketplace's minimum (a dot opens that marketplace's sellers).
// Everything comes from product.marketplace_summary (the mp_* fields computed by
// pricerest); a null summary renders nothing, and so does a summary without a
// priced marketplace in place of the band.

const RED = '#dc2626';
const GREEN = '#16a34a';

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

// Positive: the buybox is above the best marketplace (red); negative: below it (green).
const DiffPct = ({ value }) => {
    if (!isNumber(value)) return null;
    const color = value > 0 ? RED : value < 0 ? GREEN : 'inherit';
    return <span style={{ color, fontWeight: 600 }}>{`${value > 0 ? '+' : ''}${value.toFixed(2)}%`}</span>;
};

const Figure = ({ icon, color, bg, value, label, subtitle, children }) => (
    <div className="pux-metric pux-mp-figure">
        <span className="pux-metric-icon" style={{ background: bg, color }}><i className={'pi ' + icon} /></span>
        <div>
            <div className="pux-metric-lab">{label}</div>
            <div className="pux-metric-val">{value}</div>
            {subtitle && <div className="pux-mp-figure-sub">{subtitle}</div>}
            {children && <div className="pux-mp-figure-sub">{children}</div>}
        </div>
    </div>
);

// product ({id, name}) and campaignId let a band dot open the sellers dialog; without
// them the dots are plain markers.
const MarketplaceHero = ({ t, summary, config, currency, settings, campaignId, product }) => {
    const [sellersKey, setSellersKey] = useState(null);
    const [sellersVisible, setSellersVisible] = useState(false);
    if (!summary) return null;

    const firstParty = summary.mp_first_party_price;
    const buybox = summary.mp_buybox_price;
    const best = summary.mp_best_price;
    const rank = formatMarketplaceRank(summary);
    const hasBand = priceBandMarkers(summary, config).length > 0;
    const canOpenSellers = !!campaignId && !!product && product.id != null;
    // 1P texts name the operator (Seller Code); no Seller Code -> generic wording.
    const operator = operatorTextOptions(settings);

    return (
        <div className="pux-hero pux-mp-hero">
            <div className="pux-mp-figures">
                <Figure icon="pi-home" color="#2563eb" bg="#dbeafe"
                        label={t('product_detail.marketplace.first_party_price', operator)}
                        subtitle={t('product_detail.marketplace.first_party_price_sub', operator)}
                        value={isNumber(firstParty)
                            ? formatCurrency(firstParty, currency)
                            : <span className="pux-mp-muted">{t('product_detail.marketplace.no_first_party', operator)}</span>} />

                <Figure icon="pi-star" color="#16a34a" bg="#dcfce7"
                        label={t('product_detail.marketplace.buybox')}
                        subtitle={t('product_detail.marketplace.buybox_sub')}
                        value={isNumber(buybox) ? formatCurrency(buybox, currency) : <span className="pux-mp-muted">-</span>}>
                    {summary.mp_buybox_holder && <>
                        {summary.mp_buybox_holder_is_first_party &&
                            <span className="pux-chip pux-chip-blue" style={{ marginRight: 4 }}>{t('product_detail.marketplace.first_party_badge', operator)}</span>}
                        {t('product_detail.marketplace.buybox_holder')}: {summary.mp_buybox_holder}
                    </>}
                </Figure>

                <Figure icon="pi-shopping-cart" color="#d97706" bg="#fef3c7"
                        label={t('product_detail.marketplace.best_price')}
                        value={isNumber(best)
                            ? <>{formatCurrency(best, currency)}
                                {summary.mp_best_marketplace && <span className="pux-mp-at"> @ {ownAwareMarketplaceLabel(summary.mp_best_marketplace, settings)}</span>}</>
                            : <span className="pux-mp-muted">-</span>}>
                    {(rank || isNumber(summary.mp_diff_pct)) && <>
                        {rank && <span>{t('product_detail.marketplace.rank')}: <b>{rank}</b></span>}
                        {rank && isNumber(summary.mp_diff_pct) && ' · '}
                        {isNumber(summary.mp_diff_pct) && <span>{t('product_detail.marketplace.diff_pct')}: <DiffPct value={summary.mp_diff_pct} /></span>}
                    </>}
                </Figure>
            </div>

            {hasBand && (
                <div className="pux-mp-band-wrap">
                    <span className="pux-mp-band-title">{t('product_detail.marketplace.marketplaces')}</span>
                    <MarketplacePriceBand t={t} summary={summary} config={config} settings={settings} currency={currency}
                                          onSellersClick={canOpenSellers ? (key) => { setSellersKey(key); setSellersVisible(true); } : undefined} />
                </div>
            )}

            {canOpenSellers &&
                <MarketplaceSellersDialog
                    visible={sellersVisible}
                    onHide={() => setSellersVisible(false)}
                    t={t}
                    campaignId={campaignId}
                    // "Mine" in the dialog is the operator's own (1P) offer, like the list's dialog.
                    product={{ id: product.id, name: product.name, price: firstParty, currency }}
                    marketplaceKey={sellersKey}
                    label={sellersKey ? ownAwareMarketplaceLabel(sellersKey, settings) : undefined} />}
        </div>
    );
};

export default MarketplaceHero;
